import { Modal, ModalDialog, Typography, Button, Box, DialogTitle, DialogContent, DialogActions } from '@mui/joy'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { isPerTabCompanySwitchEnabled } from '@numeo/types-core'
import axios from 'axios'
import { useState } from 'react'
import { useAuth } from 'hooks/auth'
import { clearSignupGates, setAuthToken, switchAuthToken } from 'setup/SetupAxios'

interface PendingInvitation {
    invitationId: string
    projectName: string
    companyName: string
    role: number
    type: 'public' | 'private'
    expiresAt: string
    invitedBy: {
        name: string
        email: string
    }
    invitedAt: string
}

interface AcceptInvitationModalProps {
    open: boolean
    onClose: () => void
    invitation: PendingInvitation
    onSuccess?: () => void
}

export const AcceptInvitationModal = ({ open, onClose, invitation }: AcceptInvitationModalProps) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { organizationData } = useAuth()
    const isOwner = organizationData?.isOwner ?? false

    const handleAccept = async () => {
        setLoading(true)
        setError(null)

        try {
            const response = await axios.post('/application/accept-new-invitation', {
                invitationId: invitation.invitationId,
                projectName: invitation.projectName,
            })

            // Server returned but without a token — treat as a hard failure, never mutate local
            // state (same guard as CompanySwitcher.handleSwitch / AddCompanyModal).
            const { token, orgMerged, application } = response.data
            if (!token) {
                setError('Failed to accept invitation. Please try again.')
                setLoading(false)
                return
            }

            // Switch to the new company — save token and hard navigate to clear all stale state.
            // Normally sessionStorage-only (switchAuthToken), scoped to this tab, same as
            // CompanySwitcher.handleSwitch — otherwise every other open tab would follow along.
            // BUT falls back to the global setAuthToken (localStorage) in two cases:
            //   1. orgMerged: this accept just hard-deleted the accepting user's old company (the
            //      owner+cross-org branch of acceptNewInvitation), so any sibling tab still on the
            //      old company's token is now pointing at deleted data and must follow along —
            //      matching this modal's own "you will lose access to all your current companies"
            //      warning above.
            //   2. the target company itself hasn't opted into per-tab isolation yet (see
            //      ApplicationSettings.perTabCompanySwitchEnabled / CompanySwitcher.handleSwitch) —
            //      same per-company rollout gate applies here as any other switch entry point.
            const usePerTabSwitch = !orgMerged && isPerTabCompanySwitchEnabled(application)
            const persistToken = usePerTabSwitch ? switchAuthToken : setAuthToken
            persistToken(token)
            clearSignupGates()
            window.location.replace('/')
        } catch (err) {
            console.error('Failed to accept invitation:', err)
            const message = axios.isAxiosError(err) ? err.response?.data?.message : null
            setError(message || 'Failed to accept invitation. Please try again.')
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 500 }}>
                <DialogTitle>
                    {isOwner ? <WarningRoundedIcon sx={{ color: 'warning.main', mr: 1 }} /> : <InfoOutlinedIcon sx={{ color: 'primary.main', mr: 1 }} />}
                    Accept Invitation
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography level="body-md">
                            You are about to accept an invitation from <strong>{invitation.invitedBy?.email || 'a team admin'}</strong>
                            {invitation.companyName ? (
                                <>
                                    {' '}
                                    to join <strong>{invitation.companyName}</strong>
                                </>
                            ) : (
                                ''
                            )}
                            .
                        </Typography>

                        {isOwner ? (
                            <>
                                <Typography level="body-md" sx={{ color: 'danger.600', fontWeight: 600 }}>
                                    Warning: All companies in your organization and all associated data (loads, emails, settings, team members) will be
                                    permanently deleted. This action cannot be undone.
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'danger.500' }}>
                                    You will lose access to all your current companies and be moved to the new organization.
                                </Typography>
                            </>
                        ) : (
                            <Typography level="body-md" sx={{ color: 'text.primary' }}>
                                You will be added to a new company. Your current company access will remain unchanged.
                            </Typography>
                        )}

                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            You will be automatically switched to the new project after accepting.
                        </Typography>

                        {error && (
                            <Typography level="body-sm" sx={{ color: 'danger.main' }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="neutral" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="solid" color="warning" onClick={handleAccept} loading={loading}>
                        Accept Invitation
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}
